import { ReactNode, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCharacter } from '@/contexts/CharacterContext';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { useActivePersona } from '@/personas/useActivePersona';

type props = {
  children?: ReactNode;
};

/**
 * Redirectar till /onboarding EN gång när BARN-vyn är aktiv men ingen karaktär
 * valts ännu (CharacterContext.selectedCharacter === null).
 *
 * Monteras ENBART i barnvyns gren i AuthRouter (innanför CharacterScene, som i
 * sin tur bara renderas när effectiveChildMode är på). Vuxen/handledare når
 * därför aldrig hit. Vi undviker att redirecta om vi redan står på /onboarding
 * (annars loop) eller på /settings (så att "Byt karaktär"/återställning fungerar).
 */
const ChildOnboardingGate = ({ children }: props) => {
  const { selectedCharacter } = useCharacter();
  const { currentClient, isSupervising } = useContext(SupervisorContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Gaten går på PERSONA, inte på effectiveChildMode. useActivePersona bygger
  // ovanpå samma useEffectiveChildMode({ currentClient, isSupervising }) och får
  // samma options, så barn/vuxen/handledare beter sig oförändrat: persona är
  // 'child' precis när den gamla gaten var sann OCH personan inte är ungdom.
  //
  // Skillnaden gäller teen: den nås bara med childMode på (resolveActiveProfile
  // kräver effectiveChildMode för både child och teen), så den gamla gaten
  // tvingade även en ungdom utan vald karaktär in i kompis-onboardingen. Kompisen
  // hör bara till barnläget — jfr ChangeCharacterSetting och kompis-sektionen i
  // SettingsPage. Teen-vyerna (TeenHome/TeenWeek/TeenProgress) läser aldrig
  // selectedCharacter, så de fungerar utan onboarding.
  const { persona } = useActivePersona({ currentClient, isSupervising });

  const needsOnboarding =
    persona === 'child' &&
    selectedCharacter === null &&
    pathname !== '/onboarding' &&
    pathname !== '/settings';

  useEffect(() => {
    if (needsOnboarding) {
      navigate('/onboarding', { replace: true });
    }
  }, [needsOnboarding, navigate]);

  return <>{children}</>;
};

export default ChildOnboardingGate;
